// Shared, pure import contract. The Worker imports this same module so a
// browser preview cannot bypass validation or invent writable DB columns.
import { TARGET_FIELDS, FAMILY_PATCH_KEYS, STUDENT_PATCH_KEYS } from './importColumns.js';
import { cleanText, normalizeArabic, parsePhone, parseDate, parseGender, toLatinDigits, fixNationalId } from './importLogic.js';

export const empty = (v) => v == null || String(v).trim() === '';
export const equal = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
const digits = (v) => toLatinDigits(v).replace(/\D/g, '');
const fieldMap = Object.fromEntries(TARGET_FIELDS.map((f) => [f.key, f]));
export const studentFields = STUDENT_PATCH_KEYS.filter((k) => !['last_name', 'picture_url', 'tuition_fee', 'transport_fee'].includes(k));
export const familyFields = FAMILY_PATCH_KEYS;
export const label = (key) => fieldMap[key]?.label || ({ family_id: 'العائلة', section_id: 'الشعبة', transportation_id: 'الحافلة', transport_fee: 'رسم النقل' }[key]) || key;

export function mergeNotes(current, additions) {
  const lines = String(current || '').split('\n').filter(Boolean);
  for (const addition of additions) {
    for (const line of String(addition || '').split('\n').filter(Boolean)) {
      if (!lines.includes(line)) lines.push(line);
    }
  }
  return lines.join('\n');
}

export function normalizeRow(row) {
  const values = {}, issues = [], notes = [];
  const deferred = new Set(row.deferred || []);
  for (const field of TARGET_FIELDS) {
    const raw = row.values?.[field.key];
    if (empty(raw)) continue;
    let value = cleanText(raw), error = '';
    if (value.length > 4000) error = 'القيمة طويلة جداً (الحد ٤٠٠٠ حرف)';
    if (field.kind === 'phone') {
      const p = parsePhone(raw);
      if (p.status === 'ok') value = p.phone;
      else if (p.status === 'not_a_phone') value = '';
      else error = 'رقم غير صالح أو أكثر من رقم؛ صححه أو أجّله';
      if (p.label) notes.push(`${field.label}: ${p.label}`);
    } else if (field.kind === 'date') {
      const serial = typeof raw === 'number' || /^\d+$/.test(String(raw));
      const p = parseDate(row.date1904 && serial ? Number(raw) + 1462 : raw);
      if (p.status === 'ok') value = p.date;
      else error = 'تاريخ غير صالح؛ صححه أو أجّله';
    } else if (field.kind === 'gender') {
      const p = parseGender(raw);
      if (p.status === 'ok') value = p.value;
      else error = 'الجنس غير معروف؛ صححه أو أجّله';
    } else if (field.kind === 'nid') {
      value = fixNationalId(raw);
      // Not an id at all: the cell is kept in the notes, the pupil is not held up.
      if (!value && !deferred.has(field.key)) {
        notes.push(`${field.label} (للمراجعة): ${cleanText(raw)}`);
        issues.push({ field: field.key, message: 'الرقم الوطني غير صالح؛ حُفظ في الملاحظات', severity: 'warning' });
        continue;
      }
    }
    if (deferred.has(field.key)) {
      notes.push(`${field.label} (للمراجعة): ${cleanText(raw)}`);
      if (error) issues.push({ field: field.key, message: error, severity: 'warning' });
      continue;
    }
    if (error && field.key !== 'name' && value.length <= 4000) {
      // An optional cell that cannot be read is kept in the notes; it never holds the pupil up.
      notes.push(`${field.label} (للمراجعة): ${cleanText(raw)}`);
      issues.push({ field: field.key, message: `${error.split('؛')[0]}؛ حُفظ في الملاحظات`, severity: 'warning' });
      continue;
    }
    if (error) {
      issues.push({ field: field.key, message: error, severity: 'error' });
      continue;
    }
    if (value) values[field.key] = value;
  }
  if (!values.name) issues.push({ field: 'name', message: 'الاسم الكامل مطلوب', severity: 'error' });
  if (values.bus_number) notes.push(`رقم السيارة: ${values.bus_number}`);
  if (values.photo_note) notes.push(`صور شخصية: ${values.photo_note}`);
  if (values.leave_note) notes.push(`مغادرة (بيانات تاريخية): ${values.leave_note}`);
  for (const key of ['admin_app_note', 'year_result_note', 'extra_note']) {
    if (values[key]) notes.push(`${label(key)}: ${values[key]}`);
  }
  if (values.grade_title && row.sheet && /^(روضه|اول|ثاني|ثالث|رابع|خامس|سادس|سابع|ثامن|تاسع|عاشر|حادي عشر|ثاني عشر)( |$)/.test(normalizeArabic(row.sheet)) && normalizeArabic(values.grade_title) !== normalizeArabic(row.sheet)) {
    issues.push({ field: 'grade_title', message: 'الصف في الخلية مختلف عن اسم الورقة؛ راجع الشعبة المختارة', severity: 'warning' });
  }
  values.notes = mergeNotes(values.notes, notes);
  return { ...row, values, issues };
}

export function studentCandidates(values, students, families = []) {
  const familiesById = new Map(families.map((f) => [f.id, f]));
  return students.map((s) => {
    const f = familiesById.get(s.family_id) || {};
    const sameName = normalizeArabic(s.name) === normalizeArabic(values.name);
    const nid = /^\d{11}$/.test(values.national_id || '') && equal(s.national_id, values.national_id);
    const school = !empty(values.registration_number) && equal(s.registration_number, values.registration_number);
    const birth = !empty(values.birth_date) && equal(s.birth_date, values.birth_date);
    const father = !empty(values.father_name) && normalizeArabic(f.father_name) === normalizeArabic(values.father_name);
    const phone = ['father_phone', 'mother_phone'].some((k) => digits(values[k]) && [digits(f.father_phone), digits(f.mother_phone)].includes(digits(values[k])));
    const conflict = !empty(values.national_id) && !empty(s.national_id) && !equal(values.national_id, s.national_id);
    const score = nid ? 100 : sameName && birth && father ? 90 : school && sameName ? 80 : sameName && birth ? 70 : sameName ? 50 : school ? 30 : phone && father ? 20 : 0;
    return { student: s, score, conflict, strong: score >= 90 && !conflict,
      reason: nid ? 'الرقم الوطني' : sameName && birth ? 'الاسم وتاريخ الولادة' : sameName ? 'الاسم (يحتاج مراجعة)' : school ? 'الرقم المدرسي (يحتاج مراجعة)' : 'بيانات العائلة' };
  }).filter((c) => c.score).sort((a, b) => b.score - a.score);
}

export function familyCandidates(values, families) {
  return families.filter((f) => {
    const phone = ['father_phone', 'mother_phone'].some((k) => digits(values[k]) && [digits(f.father_phone), digits(f.mother_phone)].includes(digits(values[k])));
    return phone || (!empty(values.father_name) && normalizeArabic(f.father_name) === normalizeArabic(values.father_name));
  });
}

export function newFamilyKey(values, rowId) {
  // No name-only or phone-only sibling grouping.
  const parts = [normalizeArabic(values.father_name), normalizeArabic(values.last_name), digits(values.father_phone) || digits(values.mother_phone)];
  return parts.every(Boolean) ? `siblings:${parts.join('|')}` : `row:${rowId}`;
}

/**
 * The grades a school has, in order, so an import into an empty system
 * can create them correctly named and sorted. `names` are the spellings
 * used in the registers.
 */
const GRADE_CATALOG = [
  { title: 'حضانة', order: -2, primary: 1 },
  { title: 'روضة', order: -1, primary: 1 },
  { title: 'تحضيري', order: 0, primary: 1 },
  { title: 'الأول', order: 1, primary: 1 },
  { title: 'الثاني', order: 2, primary: 1 },
  { title: 'الثالث', order: 3, primary: 1 },
  { title: 'الرابع', order: 4, primary: 1 },
  { title: 'الخامس', order: 5, primary: 1 },
  { title: 'السادس', order: 6, primary: 1 },
  { title: 'السابع', order: 7, primary: 0 },
  { title: 'الثامن', order: 8, primary: 0 },
  { title: 'التاسع', order: 9, primary: 0 },
  { title: 'العاشر العلمي', order: 10, primary: 0 },
  { title: 'العاشر الأدبي', order: 10, primary: 0 },
  { title: 'الحادي عشر العلمي', order: 11, primary: 0 },
  { title: 'الحادي عشر الأدبي', order: 11, primary: 0 },
  { title: 'بكلوريا علمي', order: 12, primary: 0, names: ['علمي', 'بكالوريا علمي', 'ثاني عشر علمي', 'ثالث ثانوي علمي'] },
  { title: 'بكلوريا أدبي', order: 12, primary: 0, names: ['أدبي', 'بكالوريا أدبي', 'ثاني عشر أدبي', 'ثالث ثانوي أدبي'] },
];
// «الأول» = «أول», «العاشر العلمي» = «عاشر علمي».
export const gradeKey = (v) => normalizeArabic(v).replace(/(^| )ال/g, '$1');
export const catalogGrade = (v) => GRADE_CATALOG.find((g) => [g.title, ...(g.names || [])].some((n) => gradeKey(n) === gradeKey(v))) || null;

/**
 * «أولى م» → section «أولى» in the مسائي period. The period comes from its
 * own column when the file has one, else from the last word of the section
 * cell (the period's title, its short title or its first letter), else from
 * the period chosen for the whole import.
 */
export function sectionParts(values, batches, settings = {}) {
  const byWord = (word) => {
    const w = normalizeArabic(word);
    if (!w) return null;
    const hits = batches.filter((b) => [b.title, b.short_title].some((t) => normalizeArabic(t) === w) || (w.length === 1 && normalizeArabic(b.title).startsWith(w)));
    return hits.length === 1 ? hits[0] : null;
  };
  const words = cleanText(values.section_title).split(' ').filter(Boolean);
  const own = byWord(values.batch_title);
  const last = words.length > 1 ? byWord(words[words.length - 1]) : null;
  const batch = own || last || batches.find((b) => b.id === Number(settings.batchId)) || null;
  return { title: (last ? words.slice(0, -1) : words).join(' '), batch };
}

export function resolveSection(values, context, settings) {
  const grade = context.grades.filter((g) => normalizeArabic(g.title) === normalizeArabic(values.grade_title));
  if (grade.length !== 1) return '';
  const matches = context.sections.filter((s) => s.status === 'active' && s.grade_id === grade[0].id
    && s.academic_year_id === Number(settings.yearId) && s.batch_id === Number(settings.batchId)
    && normalizeArabic(s.title) === normalizeArabic(values.section_title));
  return matches.length === 1 ? String(matches[0].id) : '';
}

export function fieldChanges(values, existing, fields, replacements = []) {
  const replace = new Set(replacements);
  return fields.filter((key) => !empty(values[key])).map((key) => {
    const before = existing?.[key] ?? null;
    const after = key === 'notes' ? mergeNotes(before, [values[key]]) : values[key];
    const action = equal(before, after) ? 'unchanged' : empty(before) ? 'fill' : key === 'notes' ? 'append' : replace.has(key) ? 'replace' : 'keep';
    return { key, label: label(key), before, after, action };
  });
}
const patchFrom = (changes) => Object.fromEntries(changes.filter((c) => ['fill', 'replace', 'append'].includes(c.action)).map((c) => [c.key, c.after]));

/** The returned plan is the exact server-side revision the office confirms. */
export function buildPlan(input, context, role = 'register') {
  const settings = input.settings || {};
  const results = [], errors = [], ops = [], familyOps = new Map(), studentTargets = new Map(), creates = [];
  const rows = input.rows || [];
  const addError = (row, message, detail = {}) => { errors.push({ rowId: row.id, message, ...detail }); };
  const find = (list, id) => list.find((x) => x.id === Number(id));
  const staged = new Map(), autoSpecs = new Map();
  // The pupil's section as the file names it: the existing one, or (for the
  // manager) a section — and its grade — to create with this import.
  function autoSection(row) {
    const v = row.values;
    const year = find(context.years, settings.yearId);
    if (empty(v.grade_title) || empty(v.section_title) || !year) return '';
    const { title, batch } = sectionParts(v, context.batches, settings);
    if (!title || !batch) return '';
    const known = catalogGrade(v.grade_title);
    const grades = context.grades.filter((g) => [v.grade_title, known?.title].some((t) => t && gradeKey(g.title) === gradeKey(t)));
    if (grades.length > 1) return '';
    row.batchId = String(batch.id);
    if (grades.length) {
      const matches = context.sections.filter((s) => s.status === 'active' && s.grade_id === grades[0].id && s.batch_id === batch.id
        && s.academic_year_id === year.id && normalizeArabic(s.title) === normalizeArabic(title));
      if (matches.length) return matches.length === 1 ? String(matches[0].id) : '';
    }
    const gradeTitle = known?.title || cleanText(v.grade_title);
    const key = `new:auto:${gradeKey(gradeTitle)}|${batch.id}|${normalizeArabic(title)}`;
    if (!autoSpecs.has(key)) autoSpecs.set(key, { key, title, batchId: batch.id, gradeId: grades[0]?.id || '', gradeTitle: grades.length ? '' : gradeTitle, primary: !!known?.primary, order: known?.order });
    return key;
  }
  // Only references actually used by actionable students are materialized.
  function sectionFor(row) {
    if (!String(row.sectionId || '').startsWith('new:')) return find(context.sections, row.sectionId);
    const spec = (input.structures || []).find((s) => s.key === row.sectionId) || autoSpecs.get(row.sectionId);
    if (!spec || role !== 'admin') { addError(row, 'إنشاء الصفوف والشعب متاح للمدير فقط'); return null; }
    if (staged.has(spec.key)) return staged.get(spec.key);
    const year = find(context.years, settings.yearId), batch = find(context.batches, row.batchId || settings.batchId);
    let grade = find(context.grades, spec.gradeId), gradeOp = null;
    if (!grade && spec.gradeTitle?.trim()) {
      grade = context.grades.find((g) => normalizeArabic(g.title) === normalizeArabic(spec.gradeTitle));
      if (!grade) {
        const key = `grade:${normalizeArabic(spec.gradeTitle)}`;
        gradeOp = ops.find((o) => o.key === key);
        if (!gradeOp) { gradeOp = { key, table: 'grades', action: 'create', patch: { title: cleanText(spec.gradeTitle), is_primary_style: spec.primary ? 1 : 0, ...(spec.order == null ? {} : { sort_order: spec.order }) }, before: null, rowIds: [] }; ops.push(gradeOp); }
      }
    }
    if ((!grade && !gradeOp) || !year || !batch || !spec.title?.trim()) { addError(row, 'بيانات الشعبة الجديدة غير مكتملة'); return null; }
    if (grade && context.sections.some((s) => s.grade_id === grade.id && s.batch_id === batch.id && s.academic_year_id === year.id && normalizeArabic(s.title) === normalizeArabic(spec.title))) {
      addError(row, 'الشعبة موجودة؛ اخترها من القائمة'); return null;
    }
    const op = { key: spec.key, table: 'sections', action: 'create', before: null,
      label: `${grade?.title || gradeOp.patch.title} — ${cleanText(spec.title)} (${batch.title})`,
      patch: { title: cleanText(spec.title), grade_id: grade?.id || null, batch_id: batch.id, academic_year_id: year.id, status: 'active' },
      gradeKey: gradeOp?.key, rowIds: [] };
    ops.push(op);
    const section = { ...op.patch, id: spec.key, gradeKey: gradeOp?.key };
    staged.set(spec.key, section);
    return section;
  }

  for (const raw of rows) {
    const row = normalizeRow(raw);
    const result = { rowId: row.id, sheet: row.sheet, excelRow: row.excelRow, name: row.values.name || '', action: 'skip', reason: '', changes: [], warnings: row.issues.filter((i) => i.severity === 'warning').map((i) => i.message) };
    results.push(result);
    if (row.decision === 'skip') { result.reason = 'تجاوز باختيار المستخدم'; continue; }
    row.issues.filter((i) => i.severity === 'error').forEach((i) => addError(row, `${label(i.field)}: ${i.message}`, { code: 'invalid', field: i.field }));
    if (!['create', 'update'].includes(row.decision)) { addError(row, 'حدد إضافة أو تحديث أو تجاوز', { code: 'student_match' }); continue; }
    const existing = row.decision === 'update' ? find(context.students, row.studentId) : null;
    if (row.decision === 'update' && !existing) { addError(row, 'اختر الطالب الموجود'); continue; }
    if ((settings.mode === 'add' && existing) || (settings.mode === 'update' && !existing)) { addError(row, 'القرار لا يتوافق مع وضع الاستيراد'); continue; }
    const candidates = studentCandidates(row.values, context.students, context.families);
    if (!existing && candidates.length && !row.confirmNew) addError(row, 'توجد سجلات مشابهة؛ اختر طالباً أو أكد أنه طالب آخر', { code: 'student_match' });
    if (!existing && candidates.some((c) => c.reason === 'الرقم الوطني')) addError(row, 'الرقم الوطني موجود؛ اربط بالسجل الصحيح أو صحح الرقم');
    if (existing && studentTargets.has(existing.id)) addError(row, `الطالب نفسه محدد في صف آخر (${studentTargets.get(existing.id)})؛ ادمج البيانات في صف واحد أو تجاوز التكرار`);
    if (existing) studentTargets.set(existing.id, row.id);
    if (!existing) {
      const duplicate = creates.find((r) => (!empty(row.values.national_id) && equal(r.values.national_id, row.values.national_id)) || normalizeArabic(r.values.name) === normalizeArabic(row.values.name));
      if (duplicate && (!row.distinct || !duplicate.distinct || (!empty(row.values.national_id) && equal(duplicate.values.national_id, row.values.national_id)))) addError(row, `تكرار داخل الملف مع ${duplicate.id}؛ ادمج أو تجاوز، أو أكد اختلاف الشخصين`, { code: 'duplicate', rowIds: [duplicate.id, row.id] });
      creates.push(row);
    }
    let family = existing?.family_id ? find(context.families, existing.family_id) : null;
    if (row.familyId && row.familyId !== 'keep' && row.familyId !== 'new') family = find(context.families, row.familyId);
    if (row.familyId === 'new') family = null;
    if (existing && row.familyId && row.familyId !== 'keep' && !equal(family?.id, existing.family_id) && !row.confirmRelink) addError(row, 'أكد تغيير ارتباط العائلة');
    if (!family && row.familyId !== 'new' && (!existing || !existing.family_id)) addError(row, 'اختر عائلة أو إنشاء عائلة مستقلة');
    if (!family && row.familyId && !['new', 'keep'].includes(row.familyId)) addError(row, 'العائلة المحددة غير موجودة');
    if (!family && familyCandidates(row.values, context.families).length && !row.confirmNewFamily) addError(row, 'توجد عائلة مشابهة؛ اخترها أو أكد إنشاء عائلة مختلفة', { code: 'family_match' });
    const fkey = family ? `family:${family.id}` : newFamilyKey(row.values, row.separateFamily ? row.id : (row.familyGroup || row.id));
    const familyKey = !family && row.separateFamily ? `family-row:${row.id}` : fkey;
    const familyValues = Object.fromEntries(familyFields.filter((k) => k !== 'notes' && !empty(row.values[k])).map((k) => [k, row.values[k]]));
    let fop = familyOps.get(familyKey);
    if (!fop) {
      fop = { key: familyKey, table: 'families', action: family ? 'update' : 'create', existingId: family?.id || null, before: family || null, values: {}, rowIds: [], replacements: [], patch: {}, changes: [], candidateIds: familyCandidates(row.values, context.families).map((f) => f.id) };
      familyOps.set(familyKey, fop);
    }
    // Brothers and sisters share one family record. Where their rows
    // disagree, the first value stays on the family and the other is kept
    // in that pupil's own notes — nothing is lost and nobody is held up.
    const same = (key, a, b) => (fieldMap[key]?.kind === 'phone' ? digits(a) === digits(b) : normalizeArabic(a).replace(/ /g, '') === normalizeArabic(b).replace(/ /g, ''));
    const differs = [];
    for (const [key, value] of Object.entries(familyValues)) {
      if (empty(fop.values[key])) fop.values[key] = value;
      else if (!same(key, fop.values[key], value)) differs.push(`${label(key)} (مختلف عن إخوته): ${value}`);
    }
    if (differs.length) {
      row.values.notes = mergeNotes(row.values.notes, differs);
      result.warnings.push('بيانات عائلية تختلف عن إخوته؛ حُفظت في ملاحظاته');
    }
    fop.rowIds.push(row.id);
    fop.replacements.push(...(row.familyReplacements || []));
    const changes = fieldChanges(row.values, existing, studentFields, row.replacements);
    const patch = patchFrom(changes);
    if (!existing || !equal(existing.family_id, family?.id)) changes.push({ key: 'family_id', label: 'ارتباط العائلة', before: existing?.family_id || null, after: family?.id || 'عائلة جديدة', action: existing?.family_id ? 'replace' : 'fill' });
    // Never replace an identity with another student's national ID.
    if (patch.national_id && context.students.some((s) => s.id !== existing?.id && equal(s.national_id, patch.national_id))) addError(row, 'الرقم الوطني مستخدم لطالب آخر');
    let placement = null;
    if (settings.placement && !row.sectionId && !row.deferPlacement
      && !(existing && (existing.status === 'inactive' || context.enrollments.some((e) => e.student_id === existing.id && e.end_date == null)))) row.sectionId = autoSection(row);
    if (row.sectionId) {
      const section = sectionFor(row);
      const enrollment = existing ? context.enrollments.find((e) => e.student_id === existing.id && e.end_date == null) : null;
      if (!section || section.status !== 'active' || !equal(section.academic_year_id, settings.yearId) || !equal(section.batch_id, row.batchId || settings.batchId)) addError(row, 'الشعبة لا تنتمي للسنة والفوج المحددين أو مغلقة', { code: 'placement' });
      else if (existing?.status === 'inactive') addError(row, 'الطالب مغادر؛ استكمال بياناته ممكن دون تسجيله في شعبة');
      else if (!enrollment || !equal(enrollment.section_id, section.id)) {
        const date = parseDate(row.enrollmentDate || settings.enrollmentDate);
        if (date.status !== 'ok') addError(row, 'حدد تاريخ بدء القيد الصحيح');
        if (enrollment) {
          const oldSection = find(context.sections, enrollment.section_id);
          if (!row.confirmTransfer) addError(row, 'أكد نقل الطالب من شعبته الحالية');
          if (!oldSection || !equal(oldSection.grade_id, section.grade_id) || section.gradeKey) addError(row, 'النقل بين الصفوف غير مسموح في الاستيراد');
          if (date.date !== settings.today) addError(row, 'نقل طالب قائم يبدأ بتاريخ اليوم، وفق قواعد النقل');
        }
        placement = { section, before: enrollment || null, start_date: date.date };
        changes.push({ key: 'section_id', label: 'الشعبة', before: enrollment?.section_id || null, after: section.id, action: 'replace' });
      }
    } else if (settings.placement && !existing && !row.deferPlacement) addError(row, 'اختر الشعبة أو أجّل القيد صراحة', { code: 'placement' });
    if (row.busId) {
      const bus = find(context.buses, row.busId);
      if (!bus) addError(row, 'الحافلة غير موجودة');
      else if (!equal(existing?.transportation_id, bus.id)) {
        patch.transportation_id = bus.id;
        patch.transport_fee = bus.monthly_fee;
        changes.push({ key: 'transportation_id', label: 'الحافلة', before: existing?.transportation_id || null, after: bus.id, action: 'replace' },
          { key: 'transport_fee', label: 'رسم النقل', before: existing?.transport_fee || 0, after: bus.monthly_fee, action: 'replace' });
      }
    } else if (row.values.bus_number && !row.deferBus) result.warnings.push('رقم السيارة محفوظ في المصدر؛ لم تحدد حافلة');
    if (existing && !empty(row.values.registry_number) && equal(existing.registration_number, row.values.registry_number) && !equal(row.values.registration_number, row.values.registry_number)) result.warnings.push('اشتباه استيراد قديم: الرقم المدرسي الحالي يساوي رقم القيد المدني. راجع الحقلين قبل التصحيح');
    const op = { key: `student:${row.id}`, table: 'students', action: existing ? 'update' : 'create', existingId: existing?.id || null,
      before: existing || null, patch, changes, familyKey, placement, bus: row.busId ? find(context.buses, row.busId) : null,
      rowIds: [row.id], source: { sheet: row.sheet, excelRow: row.excelRow },
      // Candidate IDs are checked again server-side before a create.
      candidateIds: candidates.map((c) => c.student.id), matchValues: row.values, confirmNew: !!row.confirmNew };
    ops.push(op);
    result.action = existing ? Object.keys(patch).length || placement || !equal(existing.family_id, family?.id) ? 'update' : 'unchanged' : 'create';
    result.studentId = existing?.id || null;
    result.opKey = op.key;
    result.familyKey = familyKey;
    result.changes = changes;
  }
  for (const f of familyOps.values()) {
    f.changes = fieldChanges(f.values, f.before, familyFields, f.replacements);
    f.patch = patchFrom(f.changes);
    delete f.values; delete f.replacements;
    ops.unshift(f);
    for (const r of results.filter((r) => r.familyKey === f.key)) {
      r.familyChanges = f.changes;
      r.siblingsAffected = f.before ? context.students.filter((s) => s.family_id === f.existingId).length : f.rowIds.length;
      if (r.action === 'unchanged' && Object.keys(f.patch).length) r.action = 'update';
    }
  }
  // Shared families first, then staged grades/sections, then students.
  const rank = { families: 0, grades: 1, sections: 2, students: 3 };
  ops.sort((a, b) => rank[a.table] - rank[b.table]);
  const counts = { total: rows.length, create: 0, update: 0, unchanged: 0, skip: 0, families: [...familyOps.values()].filter((f) => f.action === 'create').length };
  results.forEach((r) => { counts[r.action]++; });
  return { results, operations: ops, errors, counts };
}
